/**
 * Every public, indexable page on defera.app that the app renders from a React
 * component: its title, description and structured data. PageSeo reads this
 * for the live page; scripts/prerender-help.mjs writes the same head into the
 * page's static HTML and lists every page in sitemap.xml. (The Help Center and
 * Security Center have their own: src/content/help/seo.js, src/content/security/seo.js.)
 *
 * A new public page goes here, or it has no metadata and isn't in the sitemap.
 * Titles: the search phrase first, under 60 characters with " | Defera".
 * Descriptions: 140–160 characters, only what the page itself says.
 */
import { ORGANIZATION, breadcrumbJsonLd, faqPageJsonLd, pageMeta } from './site.js';
import { HOME_FAQS, HOW_IT_WORKS_FAQS } from '../faqs.js';
import { CAREERS_JOBS } from '../../data/careersJobs.js';
import { SPEED_GUIDE, SPEED_GUIDE_FAQS, SPEED_GUIDE_PATH, SPEED_GUIDE_PUBLISHED } from '../guides/flightSearchSpeed.js';
import { DATA_GUIDES } from '../guides/catalog.js';
import { guidePath } from '../guides/format.js';

const HOME = { name: 'Home', path: '/' };
const GUIDES = { name: 'Guides', path: '/guides' };
const CAREERS = { name: 'Careers', path: '/careers' };

/** What Defera is, for the homepage: only what the homepage itself says. */
const serviceJsonLd = (description) => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Defera',
  serviceType: 'Book now, pay later flights in installments',
  description,
  provider: { '@id': ORGANIZATION['@id'] },
  url: 'https://defera.app/',
});

/** Article data for a guide, from what the guide itself shows. */
const articleJsonLd = ({ path, headline, description, published, modified, author }) => ({
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline,
  description,
  datePublished: published,
  dateModified: modified || published,
  author: author ? { '@type': 'Person', name: author } : { '@type': 'Organization', name: 'Defera', url: 'https://defera.app/' },
  publisher: { '@id': ORGANIZATION['@id'] },
  mainEntityOfPage: { '@type': 'WebPage', '@id': `https://defera.app${path}` },
});

/**
 * path → { name (its breadcrumb), title, description, parent?, type?, faqs?, extraJsonLd? }.
 * `faqs` must be the same list the page renders.
 */
export const PUBLIC_PAGES = [
  {
    path: '/',
    name: 'Home',
    title: 'Book Flights Now, Pay Later in Installments | Defera',
    description: 'Book flights now and pay later in weekly or bi-weekly installments, with no credit check and no interest. Lock in today’s price, with $0 down on select fares.',
    faqs: HOME_FAQS,
    extraJsonLd: (p) => [serviceJsonLd(p.description)],
  },
  {
    path: '/how-it-works',
    name: 'How It Works',
    title: 'How Flight Layaway Works: Payment Plans & FAQs',
    description: 'Defera is flight layaway: book at today’s price and pay weekly or bi-weekly. Compare both plans, see example schedules, and learn what happens if plans change.',
    faqs: HOW_IT_WORKS_FAQS,
  },
  {
    path: '/about',
    name: 'About Us',
    title: 'About Us: Travel, Made Possible for Everyone',
    description: 'Defera was founded in 2024 in San Francisco to make air travel accessible to everyone, with transparent pricing and flexible payments. Meet the team behind it.',
  },
  {
    path: '/charter',
    name: 'Our Charter',
    title: 'Our Charter: Principles and Commitments',
    description: 'The principles and commitments that guide Defera: no credit checks, price locks we honor, clear updates during disruptions, and how to hold us accountable.',
  },
  {
    path: '/careers',
    name: 'Careers',
    title: 'Careers: Come Build the Future of Travel',
    description: 'Join a small San Francisco team making flights accessible to everyone, with no credit checks and no predatory interest. See open roles, perks and how we work.',
  },
  {
    path: '/contact',
    name: 'Contact',
    title: 'Contact Us: Chat, Call or Send a Message',
    description: 'Reach Defera by chat or by phone 24/7, or send our team a message from this page for a reply within 24 hours. You can also write to our San Francisco address.',
  },
  {
    path: '/customers',
    name: 'Customer Stories',
    title: 'Customer Stories: Real People, Real Journeys',
    description: 'See how everyday travelers use Defera to visit family, chase opportunities and make memories, booking flights now and paying over time with no credit check.',
  },
  {
    path: '/press',
    name: 'Press & Media',
    title: 'Press & Media: Company Facts and Story Ideas',
    description: 'Resources for journalists covering travel, fintech and consumer technology: Defera’s company overview, story ideas, and how to request a press kit or interview.',
  },
  {
    path: '/newsroom',
    name: 'Newsroom',
    title: 'Newsroom: News, Updates and Milestones',
    description: 'The latest news, updates and milestones from Defera, with press releases on our beta launch, price protection and no-credit-check booking, plus media contacts.',
  },
  {
    path: '/terms',
    name: 'Terms of Service',
    title: 'Terms of Service',
    description: 'The Defera Terms of Service: how booking, installment payments, price protection, cancellations, refunds, defaults and disputes work between you and Defera LLC.',
  },
  {
    path: '/privacy',
    name: 'Privacy Policy',
    title: 'Privacy Policy',
    description: 'How Defera collects, uses and protects your personal information, how we work with ad and analytics partners, and your rights to access, delete or opt out.',
  },
  {
    path: '/privacy-choices',
    name: 'Your Privacy Choices',
    title: 'Your Privacy Choices: Do Not Sell or Share',
    description: 'Opt out of the sale or sharing of your personal information with Defera’s advertising and analytics partners. We also honor Global Privacy Control.',
  },
  {
    path: '/guides',
    name: 'Guides',
    title: 'Travel Guides & Research: Paying for Flights',
    description: 'Guides and original research on booking and paying for flights: pay-later options, bad credit, fare holds, when to book, and what cheap-flight sites really save.',
  },
  // No /reserve: Reserve is closed to new customers, so the page sends
  // everyone else (Googlebot too) to the homepage, and Google could never
  // index it ("Page with redirect", Search Console 2026-10-30). It's noindex
  // in vercel.json; the terms stay public for the customers it covers.
  {
    path: '/reserve/terms',
    name: 'Defera Reserve Terms',
    title: 'Defera Reserve Terms & Conditions',
    description: 'The terms for Defera Reserve, a closed-loop stored value program for travel: adding funds, eligible uses, no withdrawals, no price guarantee and no expiration.',
  },
  {
    path: '/explore',
    name: 'Explore',
    title: 'Explore Destinations With Flexible Dates',
    description: 'Enter your departure airport to discover where you can fly, with flexible or specific dates, on a map. See each destination’s fare and what you’d pay today.',
  },
  {
    path: '/rewards',
    name: 'Defera Miles',
    title: 'Defera Miles: Earn 2 Miles per Dollar on Travel',
    description: 'Earn 2 Defera Miles per dollar on flights, hotels and packages. Use them on your next trip or redeem e-gift cards at 200+ merchants. Your Miles never expire.',
  },
  {
    path: SPEED_GUIDE_PATH,
    parent: GUIDES,
    type: 'article',
    name: 'Fastest Flight Search Engines',
    title: 'Fastest Flight Search Engines of 2026, Tested',
    description: 'We timed 19 flight search engines, from Google Flights and Kayak to Expedia and Priceline, on six routes. See which show flight results fastest.',
    faqs: SPEED_GUIDE_FAQS,
    extraJsonLd: (p) => [articleJsonLd({ path: p.path, headline: SPEED_GUIDE.title, description: p.description, published: SPEED_GUIDE_PUBLISHED, author: SPEED_GUIDE.author })],
  },
  {
    path: '/guides/defera-vs-affirm-klarna-uplift',
    parent: GUIDES,
    type: 'article',
    name: 'Defera vs Affirm, Klarna & Uplift',
    title: 'Defera vs Affirm vs Klarna vs Uplift for Flights',
    description: 'Compare Defera with Affirm, Klarna and Uplift (now Flex Pay) for flights: credit checks, interest, payment schedules and when you get your airline confirmation.',
  },
  {
    path: '/guides/fintech-installment-flight-booking',
    parent: GUIDES,
    type: 'article',
    name: 'Fintech Options for Flight Installments',
    title: 'Flight Installment Plans: Fintech Options Compared',
    description: 'Compare flight installment plans from Affirm, Klarna, airline programs and layaway-style services: payment schedules, interest, deposits and how to lock a fare.',
  },
  {
    path: '/guides/flight-installments-bnpl-payment-options',
    parent: GUIDES,
    type: 'article',
    name: 'Flight Installments Without a Credit Card',
    title: 'Book Flights on Installments Without a Credit Card',
    description: 'Learn how to finance airline tickets with BNPL apps, debit cards, PayPal installments, and flight-specific payment plans—no credit card needed.',
  },
  {
    path: '/guides/flight-layaway-vs-bnpl',
    parent: GUIDES,
    type: 'article',
    name: 'Flight Layaway vs BNPL',
    title: 'Flight Layaway vs BNPL: How Flights on Layaway Work',
    description: 'What flight layaway is and how it differs from buy now, pay later: when your booking is confirmed, fees, interest and credit checks, and which suits your trip.',
  },
  {
    path: '/guides/lock-flight-price-with-deposit',
    parent: GUIDES,
    type: 'article',
    name: 'Lock in a Flight Price With a Deposit',
    title: 'Flight Deposits: How to Lock In a Flight Price',
    description: 'Lock in a flight price with a deposit, fare hold, price freeze or layaway-style plan. Compare how each works, what it costs and what to check before you pay.',
  },
  {
    path: '/guides/pay-flights-with-gift-cards-credits',
    parent: GUIDES,
    type: 'article',
    name: 'Pay for Flights With Gift Cards & Credits',
    title: 'How to Use Airline Gift Cards & Travel Credits',
    description: 'How to use airline gift cards and travel credits for flights: expiry and transfer rules to check, and how to apply them at checkout and pay the rest over time.',
  },
  {
    path: '/guides/semi-private-flights-bnpl',
    parent: GUIDES,
    type: 'article',
    name: 'Semi-Private Flights on Installments',
    title: 'Book Semi-Private Flights on Installments',
    description: 'Why fly semi-private, which buy now, pay later providers support semi-private flights, and how to book a semi-private flight with Defera and pay over time.',
  },
  ...DATA_GUIDES.map((g) => ({
    path: guidePath(g.slug),
    parent: GUIDES,
    type: 'article',
    name: g.title,
    title: g.seoTitle,
    description: g.description,
    faqs: g.faqs,
    lastmod: g.updated,
    extraJsonLd: (p) => [articleJsonLd({ path: p.path, headline: g.title, description: p.description, published: g.published, modified: g.updated, author: g.author })],
  })),
];

/** Descriptions for the open positions, from what each listing shows (title, team, location, pay). */
const CAREER_DESCRIPTIONS = {
  'concierge-specialist': 'Defera is hiring a Concierge Specialist (Customer Experience), remote or in New York, to help travelers with bookings and payments. Pay range $45,000–$55,000.',
  'regional-director': 'Defera is hiring a Regional Director (Operations) in San Francisco to own West Coast operations. Base salary $120,000–$150,000, plus equity and benefits.',
};

/** One page per open position (src/data/careersJobs.js). No JobPosting data: the listings carry no posting dates. */
function careerPages() {
  return CAREERS_JOBS.map((job) => {
    const full = `${job.title}, ${job.location}`;
    return {
      path: `/careers/${job.slug}`,
      name: job.title,
      parent: CAREERS,
      title: `${full} | Defera`.length <= 60 ? full : job.title,
      description: CAREER_DESCRIPTIONS[job.slug] || `Defera is hiring a ${job.title} (${job.department}) in ${job.location}. See what the role involves, what we’re looking for and how to apply.`,
    };
  });
}

/** Every public page, in sitemap order. */
export function allPublicPages() {
  return [...PUBLIC_PAGES, ...careerPages()];
}

/** The full metadata for a public page, or null when the path isn't one. */
export function metaForPath(path) {
  const page = allPublicPages().find((p) => p.path === path);
  if (!page) return null;
  const crumbs = page.path === '/' ? [] : [HOME, ...(page.parent ? [page.parent] : []), { name: page.name, path: page.path }];
  return pageMeta({
    path: page.path,
    title: page.title,
    description: page.description,
    type: page.type,
    jsonLd: [
      ...(page.extraJsonLd ? page.extraJsonLd(page) : []),
      ...(page.faqs ? [faqPageJsonLd(page.faqs)] : []),
      ...(crumbs.length ? [breadcrumbJsonLd(crumbs)] : []),
    ],
  });
}
